import {
  AlertTriangleIcon,
  CheckIcon,
  InfoCircleIcon,
  LoadingDIcon,
  XCloseIcon,
  NewIconVariant
} from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import utilify from '@prophecy/utils/react/utilify';
import { Provider, Description, Close, Action } from '@radix-ui/react-toast';
import { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';

import { ABOVE_FULLSCREEN_DIALOG_Z_INDEX, isFullScreenDialogOpen } from '../FullScreenDialog/utils';
import {
  StyledToastContainer,
  StyledStack,
  StyledViewPort,
  containerStyle,
  ContentWrapper,
  ActionWrapper,
  AbsoluteActionWrapper,
  CloseButton
} from './styled';
import { ToastPlacement, ToastPlacementType, ToastProps, Variant, ToastUtilReturnType, ToastUtil } from './types';

const toastLabel = 'toastRootContainer';
const topId = 'toastTopContainer';
const bottomId = 'toastBottomContainer';

const IconMap = {
  loading: { icon: LoadingDIcon, type: NewIconVariant.extended },
  error: { icon: AlertTriangleIcon, type: NewIconVariant.default },
  success: { icon: CheckIcon, type: NewIconVariant.default },
  info: { icon: InfoCircleIcon, type: NewIconVariant.default },
  warning: { icon: AlertTriangleIcon, type: NewIconVariant.default },
  plain: null
};

export const MAX_TOAST_DURATION = 10000000 * 1000 * 1000;
// max THRESHOLD to avoid closing toast on drag
const MAX_TOAST_SWIPE_THRESHOLD = 100000000;

export function useBodyBoundary(enable?: boolean) {
  const [initialBoundary, setInitialBoundary] = useState<{
    top: number;
    left: number;
    right: number;
    bottom: number;
  }>();
  const domRef = useRef<HTMLLIElement | null>(null);

  const element = domRef.current;
  useEffect(() => {
    if (!element || !enable) return;

    const { top, left, width, height } = element.getBoundingClientRect();
    setInitialBoundary({
      top: -top,
      left: -left,
      right: window.innerWidth - (left + width),
      bottom: window.innerHeight - (top + height)
    });
  }, [element, enable]);

  return { ref: domRef, bound: initialBoundary };
}

export const Toast = ({
  content,
  duration = 6000,
  closeable = true,
  width = '400px',
  variant = Variant.info,
  placement = ToastPlacement.bottom,
  open,
  isRelative = false,
  onClose,
  actions,
  draggable,
  showIcon = true,
  contentStyle,
  zIndex,
  placementOffset,
  ...restProps
}: ToastProps) => {
  const { icon: Icon, type: iconType } = IconMap[variant] || {};
  const { ref, bound } = useBodyBoundary(draggable && open);
  const onOpenChange = (open: boolean) => {
    if (!open && onClose) {
      onClose();
    }
  };
  if (!open) {
    //Setting the content takes some time so loader shown till the time.
    return null;
  }
  const _duration = closeable ? duration : MAX_TOAST_DURATION;
  const ActionWrapperComponent = variant === Variant.plain ? AbsoluteActionWrapper : ActionWrapper;
  const closeButton = closeable ? (
    <Close asChild>
      <CloseButton
        icon={<XCloseIcon type='default' />}
        variant='linkGray'
        aria-label='Close toast'
        data-test-id='toast-close-button'
      />
    </Close>
  ) : null;
  const _actions =
    actions || closeButton ? (
      <ActionWrapperComponent>
        {actions}
        {closeButton}
      </ActionWrapperComponent>
    ) : null;

  const toastContainer = (
    <StyledToastContainer
      aria-label='toast-container'
      data-state={variant}
      {...restProps}
      variant={variant}
      width={width}
      open={open}
      isDraggable={draggable}
      ref={ref}
      onOpenChange={onOpenChange}
      data-test-id={restProps['data-test-id'] ?? 'toast-container'}>
      <Description asChild>
        <StyledStack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
          {showIcon && Icon ? (
            <span>
              {/** @ts-ignore ts not able to narrow down types for Icon/IconType */}
              <Icon type={iconType} className={variant} spin={variant === Variant.loading} />
            </span>
          ) : null}
          <ContentWrapper
            direction='horizontal'
            gap={theme.spaces.x12}
            alignY='center'
            style={contentStyle}
            data-test-id={
              restProps['data-test-id'] ? `${restProps['data-test-id']}-content-wrapper` : 'toast-content-wrapper'
            }>
            {content}
          </ContentWrapper>
          {variant !== Variant.plain ? _actions : null}
        </StyledStack>
      </Description>
      <Action altText='esc' asChild />
      {variant === Variant.plain ? _actions : null}
    </StyledToastContainer>
  );

  return (
    <Provider duration={_duration} swipeThreshold={MAX_TOAST_SWIPE_THRESHOLD}>
      {draggable ? <Draggable bounds={bound}>{toastContainer}</Draggable> : toastContainer}
      <StyledViewPort
        label={toastLabel}
        isRelative={isRelative}
        zIndex={zIndex}
        placement={placement}
        placementOffset={placementOffset ?? 0}
      />
    </Provider>
  );
};

const setTopAndBottomToastContainer = (
  id: string,
  placement: ToastPlacementType = ToastPlacement.bottom,
  placementOffset: number = 0,
  zIndex?: string
) => {
  const wrapper = document.createElement('div');
  wrapper.setAttribute('id', id);
  wrapper.setAttribute('style', containerStyle(placement, placementOffset, zIndex));
  const currentElement = document.getElementById(id);
  if (!currentElement) {
    document.querySelector('body')?.append(wrapper);
  } else {
    currentElement.setAttribute('style', containerStyle(placement, placementOffset, zIndex));
  }
};

const currentOpenedToastCloseHandlers: Array<() => void> = [];

export const toast = utilify<ToastProps, ToastUtilReturnType>(({ props, render, unmount }) => {
  let currentProps: ToastProps = { ...props, open: true };
  const placement = props.placement || ToastPlacement.bottom;
  const isTop = placement === ToastPlacement.top;

  // When no explicit z-index is passed and a FullScreenDialog is open, raise the toast
  // above the dialog so it stays visible instead of being covered by the dialog overlay.
  const zIndex = props.zIndex ?? (isFullScreenDialogOpen() ? ABOVE_FULLSCREEN_DIALOG_Z_INDEX : undefined);

  setTopAndBottomToastContainer(isTop ? topId : bottomId, placement, props.placementOffset, zIndex);

  function renderToast(_props: ToastProps) {
    currentProps = _props;
    render(
      <Toast
        {..._props}
        isRelative={true}
        onClose={() => {
          unmount();
          currentOpenedToastCloseHandlers.splice(currentOpenedToastCloseHandlers.indexOf(close), 1);
          props.onClose?.();
        }}
      />,
      { container: isTop ? '#' + topId : '#' + bottomId, unmountOnNavigation: true }
    );
  }

  const update = (props: Partial<ToastProps>) => renderToast({ ...currentProps, open: true, ...props });
  const close = () => renderToast({ ...currentProps, open: false });
  currentOpenedToastCloseHandlers.push(close);

  // render initially
  renderToast(currentProps);

  // return utilities to update and close it
  return { update, close };
}) as ToastUtil;

toast.success = (props) => {
  return toast({ ...props, variant: Variant.success });
};

toast.error = (props) => {
  return toast({ ...props, variant: Variant.error });
};

toast.loading = (props) => {
  return toast({ ...props, variant: Variant.loading });
};

toast.info = (props) => {
  return toast({ ...props, variant: Variant.info });
};

toast.plain = (props) => {
  return toast({ ...props, variant: Variant.plain });
};

toast.closeAll = () => {
  // open:false skips onClose, so drain before closing or stale handlers re-close replaced toasts
  currentOpenedToastCloseHandlers.splice(0).forEach((handler) => handler());
};
